/**
 * Chiffrement des données de compte : PBKDF2-SHA256 → AES-GCM-256.
 * Tout se passe côté client ; aucun secret n'est envoyé à un serveur.
 */

export const KDF_ITERATIONS = 600_000;

export interface EncryptedData {
  /** IV aléatoire, encodé en base64. */
  iv: string;
  /** Données chiffrées, encodées en base64. */
  data: string;
}

const VERIFIER_TEXT = 'terminal.nsi.xyz';

export function randomBytes(length: number): Uint8Array<ArrayBuffer> {
  const bytes = new Uint8Array(length);
  crypto.getRandomValues(bytes);
  return bytes;
}

export function toBase64(bytes: Uint8Array): string {
  let binary = '';
  const chunkSize = 0x8000;
  for (let index = 0; index < bytes.length; index += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(index, index + chunkSize));
  }
  return btoa(binary);
}

export function fromBase64(value: string): Uint8Array<ArrayBuffer> {
  const binary = atob(value);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return bytes;
}

export function newSalt(): string {
  return toBase64(randomBytes(16));
}

export async function deriveKey(
  password: string,
  salt: string,
  iterations: number = KDF_ITERATIONS,
): Promise<CryptoKey> {
  const material = await crypto.subtle.importKey(
    'raw',
    new TextEncoder().encode(password),
    'PBKDF2',
    false,
    ['deriveKey'],
  );
  return crypto.subtle.deriveKey(
    { name: 'PBKDF2', salt: fromBase64(salt), iterations, hash: 'SHA-256' },
    material,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  );
}

export async function encryptText(key: CryptoKey, text: string): Promise<EncryptedData> {
  const iv = randomBytes(12);
  const ciphertext = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    key,
    new TextEncoder().encode(text),
  );
  return { iv: toBase64(iv), data: toBase64(new Uint8Array(ciphertext)) };
}

export async function decryptText(key: CryptoKey, encrypted: EncryptedData): Promise<string> {
  const plaintext = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: fromBase64(encrypted.iv) },
    key,
    fromBase64(encrypted.data),
  );
  return new TextDecoder().decode(plaintext);
}

export async function createAccountSecrets(
  password: string,
  salt: string,
  iterations: number = KDF_ITERATIONS,
): Promise<{ key: CryptoKey; verifier: EncryptedData }> {
  const key = await deriveKey(password, salt, iterations);
  return { key, verifier: await encryptText(key, VERIFIER_TEXT) };
}

export async function createVerifier(
  password: string,
  salt: string,
  iterations: number = KDF_ITERATIONS,
): Promise<EncryptedData> {
  return (await createAccountSecrets(password, salt, iterations)).verifier;
}

/**
 * Vérifie le mot de passe et renvoie la clé de chiffrement (ou `null`).
 * La clé reste en mémoire, jamais stockée.
 */
export async function unlockKey(
  password: string,
  salt: string,
  iterations: number,
  verifier: EncryptedData,
): Promise<CryptoKey | null> {
  const key = await deriveKey(password, salt, iterations);
  try {
    const text = await decryptText(key, verifier);
    return text === VERIFIER_TEXT ? key : null;
  } catch {
    return null;
  }
}
